import axios from 'axios';
import toast from 'react-hot-toast';

import { api as config } from '../../global/config/api';
import { ApiError } from './error';
import { broadcastLogout, readCsrfCookie } from './session';
import { createCooldown, rateLimitMessage } from './rate-limit';

// TSS vite/04 — the canonical HTTP client. Layer 1.
// All app code goes through this instance (directly or via useQuery / useBridge).

const MUTATING = new Set(['post', 'put', 'patch', 'delete']);
const cooldown = createCooldown();

export const api = axios.create({
    baseURL: config.endpoint,
    timeout: 15_000,
    withCredentials: true,
});

api.interceptors.request.use((cfg) => {
    const remaining = cooldown.remaining();
    if (remaining > 0) throw new ApiError({ status: 429, code: 'LOCAL_RATE_LIMIT',
        message: rateLimitMessage(remaining), retryAfter: remaining });
    inferContentType(cfg);

    if (MUTATING.has(String(cfg.method).toLowerCase())) {
        const csrf = readCsrfCookie();
        if (csrf) cfg.headers['X-CSRF-Token'] = csrf;
    }

    cfg.metadata = { id: crypto.randomUUID(), start: performance.now() };
    cfg.headers['X-Request-ID'] = cfg.metadata.id;

    return cfg;
});

api.interceptors.response.use(
    (res) => {
        if (import.meta.env.DEV) {
            const ms = (performance.now() - res.config.metadata.start).toFixed(0);
            console.debug(`[api] ${res.config.method?.toUpperCase()} ${res.config.url} → ${res.status} (${ms}ms)`);
        }
        return res;
    },
    async (error) => {
        const apiError = ApiError.fromAxios(error);

        if (apiError.status === 401) handle401(error);
        if (apiError.status === 403) handle403();
        if (apiError.status === 429 && apiError.code !== 'LOCAL_RATE_LIMIT') {
            cooldown.pause(apiError.retryAfterMs);
            toast.error(apiError.message, { id: 'api-rate-limit', duration: 6000 });
        }

        throw apiError;
    },
);

// TSS vite/04 §"Content-type auto-detection". Sniffs cfg.data and sets
// (or removes) the Content-Type header. Caller never sets it manually.
function inferContentType(cfg) {
    if (cfg.data == null) return;
    if (cfg.headers['Content-Type']) return;

    if (cfg.data instanceof FormData) {
        // Browser must set the multipart boundary; pre-setting breaks it.
        delete cfg.headers['Content-Type'];
        return;
    }
    if (cfg.data instanceof Blob) {
        cfg.headers['Content-Type'] = cfg.data.type || 'application/octet-stream';
        return;
    }
    if (cfg.data instanceof URLSearchParams) {
        cfg.headers['Content-Type'] = 'application/x-www-form-urlencoded';
        return;
    }
    if (cfg.data instanceof ArrayBuffer || ArrayBuffer.isView(cfg.data)) {
        cfg.headers['Content-Type'] = 'application/octet-stream';
        return;
    }
    if (typeof cfg.data === 'object') {
        cfg.headers['Content-Type'] = 'application/json';
    }
}

// Per TSS vite/05 §"Global error side effects" — only 401 and 403
// trigger global behavior. Everything else is the caller's job.

// Matches the auth namespace whether the caller wrote 'auth/me' (relative to
// baseURL, which is how this project writes them), '/auth/me', or a full URL.
//
// The template tested `url.includes('/auth/')`, and that leading slash is the
// whole bug: our paths carry none, so /auth/me answering 401 did NOT bypass.
// Every boot without a session ended in broadcastLogout() plus a hard load of
// /login, which booted the app, which called /auth/me again — a reload loop
// with the login screen never getting to render. Same reason a wrong password
// reloaded the page instead of showing "Credenciales inválidas".
const AUTH_ROUTE = /(^|\/)auth\//;

function handle401(error) {
    const url = error?.config?.url || '';
    // /auth/* requests bypass — otherwise /auth/me returning 401 loops.
    if (AUTH_ROUTE.test(url)) return;

    // Seatbelt: already standing on the login screen, there is nowhere to send
    // anyone. Without this, any other 401 during login would loop the same way.
    if (window.location.pathname === '/login') return;

    broadcastLogout();
    // AuthProvider listens for 'logout_signal' storage events and clears state.
    window.location.href = '/login';
}

let forbiddenToastActive = false;
function handle403() {
    if (forbiddenToastActive) return;
    forbiddenToastActive = true;
    toast.error('No tienes permiso para esta acción');
    setTimeout(() => { forbiddenToastActive = false; }, 2000);
    window.dispatchEvent(new CustomEvent('auth:permissions-refresh'));
}

// Convenience helpers. TSS vite/04 §"File uploads" / §"File downloads".

export async function uploadFile(endpoint, file, fields = {}, opts = {}) {
    const { onProgress, signal, fileField = 'file' } = opts;
    const form = new FormData();
    form.append(fileField, file);
    for (const [k, v] of Object.entries(fields)) form.append(k, String(v));
    const res = await api.post(endpoint, form, {
        onUploadProgress: onProgress,
        signal,
    });
    return res.data;
}

export async function downloadFile(endpoint, opts = {}) {
    const res = await api.get(endpoint, {
        responseType: 'blob',
        signal: opts.signal,
    });
    return res.data;
}
